import { useMemo } from 'react'

import { TeamMemberRole, UserSettingsUiTextRatioCalculation } from '@api'
import { zodResolver } from '@hookform/resolvers/zod'
import { useUnit } from 'effector-react'
import { FormProvider, useForm } from 'react-hook-form'
import { useQueryClient } from 'react-query'
import * as z from 'zod'

import InfoIcon from '@/assets/icons/icon-info.svg'
import { Page } from '@/components/layout/page/page.tsx'
import type { SelectOptionsType } from '@/components/ui/form/FormSelect'
import { FormSelect } from '@/components/ui/form/FormSelect'
import { FormSwitch } from '@/components/ui/form/form-switch.tsx'
import { Separator } from '@/components/ui/separator.tsx'
import { useToast } from '@/components/ui/toast.tsx'
import { Tooltip } from '@/components/ui/tooltip.tsx'
import { $account, $isNSFWOn, setIsNSFWOn } from '@/models/account.ts'
import { useAccount } from '@/modules/accounts/hooks/useAccounts.ts'
import { useTimezones } from '@/modules/profile-settings/hooks/use-timezones.ts'
import { handleError } from '@/utils/error'
import { useDebouncedCallback } from '@/utils/useDebouncedCallback.ts'

const GeneralSettingsSchema = z.object({
  showCents: z.boolean(),
  textRatioCalculation: z.nativeEnum(UserSettingsUiTextRatioCalculation),
  timezone: z.string(),
  nfswOn: z.boolean(),
})

type GeneralSettings = z.infer<typeof GeneralSettingsSchema>

const textRatioCalculationOptions: SelectOptionsType = [
  {
    label: 'Use new subs revenue',
    value: UserSettingsUiTextRatioCalculation.USE_NEW_SUBS_REVENUE,
  },
  {
    label: 'Use all subs revenue',
    value: UserSettingsUiTextRatioCalculation.USE_ALL_SUBS_REVENUE,
  },
]

export const GeneralSettingsTab = () => {
  const queryClient = useQueryClient()
  const { userSettings, updateUserSettings } = useAccount()
  const { timezones, setTimezone } = useTimezones()
  const account = useUnit($account)
  const nfswOn = useUnit($isNSFWOn)
  const timezoneOptions = useMemo(
    () =>
      timezones?.zones
        ?.filter((zone) => zone.displayName && zone.zoneId)
        .map((zone) => {
          return { label: zone.displayName!, value: zone.zoneId! }
        }) ?? [],
    [timezones],
  )
  const toast = useToast()

  const form = useForm<GeneralSettings>({
    mode: 'onChange',
    resolver: zodResolver(GeneralSettingsSchema),
    defaultValues: {
      showCents: userSettings?.showCents,
      textRatioCalculation: userSettings?.textRatioCalculation,
      timezone: account?.timezone,
      nfswOn: !!nfswOn,
    },
  })

  const { handleSubmit, control } = form

  const onSubmit = useDebouncedCallback(
    500,
    (values: GeneralSettings) => {
      setIsNSFWOn(values.nfswOn)

      if (values.timezone !== account?.timezone) {
        setTimezone
          .mutateAsync(values.timezone)
          .then(() => {
            toast.success(`Timezone was changed successfully`)
          })
          .catch((error) => {
            handleError(error, 'There was an error changing the timezone.')
          })
          .finally(() => {
            queryClient.refetchQueries('account')
          })
      }

      const changedShowCents = values.showCents !== userSettings?.showCents

      updateUserSettings({ ...values }, () => {
        if (changedShowCents) {
          toast.success(
            `Show Cents has been ${values.showCents ? 'enabled' : 'disabled'}`,
          )
        }
      })
    },
    [updateUserSettings, toast, userSettings?.showCents, account?.timezone],
  )

  return (
    <Page
      title="Preferences"
      description="Manage your personal preferences. These settings will only be applied for you."
    >
      <div className="border-t pb-8" />
      <FormProvider {...form}>
        <form onChange={handleSubmit(onSubmit)}>
          <FormSelect
            className="flex items-start justify-between lg:flex-row lg:items-center"
            labelTextClassName="text-xl"
            label="Texting Ratio Calculation"
            name="textRatioCalculation"
            options={textRatioCalculationOptions}
            contentClassName="w-full lg:w-1/2"
          />
          <Separator className="my-4" />
          <FormSwitch
            labelClassName="justify-between"
            labelTextClassName="text-xl"
            name="showCents"
            control={control}
            label="Show Cents in the Dashboard"
            labelBeforeSwitch
          />
          <Separator className="my-4" />
          <FormSwitch
            labelClassName="justify-between"
            labelTextClassName="text-xl"
            name="nfswOn"
            control={control}
            label="NSFW Filter"
            disabled={account?.nsfwAlwaysOn}
            labelBeforeSwitch
          />
          {account?.timezone &&
            account?.memberRole === TeamMemberRole.LEADER && (
              <>
                <Separator className="my-4" />
                <FormSelect
                  className="flex items-start justify-between lg:flex-row lg:items-center"
                  labelTextClassName="text-xl flex gap-2"
                  label={
                    <>
                      Agency Timezone{' '}
                      <Tooltip
                        content="Determines the timezone for all dashboard statistics. This defines when a new day starts (00:00) in your charts. Note: The Accounting page is excluded and always remains in UTC+00:00 to match OnlyFans."
                        contentClassName="max-w-[280px] text-left"
                        side="bottom"
                      >
                        <InfoIcon className="h-6 w-6" />
                      </Tooltip>
                    </>
                  }
                  name="timezone"
                  options={timezoneOptions}
                  contentClassName="w-full lg:w-1/2"
                />
              </>
            )}
        </form>
      </FormProvider>
    </Page>
  )
}
